import { useEffect } from 'react';

import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { closeSnackbar, SnackbarProvider } from 'notistack';
import { I18nextProvider } from 'react-i18next';
import { RouterProvider, createBrowserRouter, useRouteError } from 'react-router-dom';

import { login } from '~constants';
import { useSession } from '~hooks';
import { GeneralLoaderWrapper } from '~layouts';
import dayjs from '~lib/dayjs-setup';
import i18n, { getLanguageFromCulture } from '~lib/i18n';

import routes from './routes';
import AppTheme from './themes/theme';

import './app.css';
import 'dayjs/locale/nl';

const DevErrorBoundary = () => {
	throw useRouteError();
};

const LanguageSynchronizer = () => {
	const { user } = useSession();

	useEffect(() => {
		const preferredLanguage = getLanguageFromCulture(user?.culture);
		dayjs.locale(preferredLanguage);
		i18n.changeLanguage(preferredLanguage);
	}, [user?.culture]);

	return null;
};

/**
 * Setup the app and its routing
 */
const App = () => {
	// Hook for getting/setting language from searchparams?
	const theme = AppTheme();

	const loaderWrapper = (
		<I18nextProvider i18n={i18n}>
			<LanguageSynchronizer />
			<GeneralLoaderWrapper />
		</I18nextProvider>
	);

	login.msalInstance.initialize();

	const router = createBrowserRouter([
		{
			// When developing we may want to use the vite error overlay
			// But by default react-router will handle these, what we don't
			// want in development. So propagate the error upstream
			ErrorBoundary: import.meta.env.DEV ? DevErrorBoundary : undefined,
			hydrateFallbackElement: loaderWrapper,
			children: routes(),
		},
	]);

	return (
		<ThemeProvider theme={theme}>
			<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='nl'>
				<I18nextProvider i18n={i18n}>
					<LanguageSynchronizer />
					<CssBaseline />
					<SnackbarProvider
						anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
						style={{
							...theme.typography.paragraph,
							maxWidth: '350px',
						}}
						onEnter={(node, _, key) => {
							node.addEventListener('click', () => {
								closeSnackbar(key);
							});
						}}
					>
						<div className='app'>
							<RouterProvider router={router} />
						</div>
					</SnackbarProvider>
				</I18nextProvider>
			</LocalizationProvider>
		</ThemeProvider>
	);
};

export default App;
